import { chainRows, directNeeds, gridColumns, gridRows, type GridPlan, type GridRanks, type GridRows, type GridSpace, type GridStep } from '@nsb/core';
import { BOARD_ROOM, bevelled, pathOf, routeLines, type Pt, type RouteBox } from './routes.ts';
import { channelTracks, type ChannelRun } from './channels.ts';
import { linesOf, needKeys, type TileLine, type TilePhase, type TileStep } from './model.ts';
import { chainThrough, docksOf, linesThrough, placesOf, type Transit } from './transitions.ts';

/**
 * The board in pixels (design studies 09 and 10): full size, never zoomed, as wide as the page it is in, or as what it
 * draws needs (a row is never carried on under itself, P-19, and steps are spread as far apart as their lines need,
 * #78: then the board scrolls across in its frame). Each phase a white block between its start line and its end line
 * (P-17, Jason, 2026-10-09: "each phase should be white with the start and end lines and then the phase transition node
 * is locked to the center and between both, with the phase title just above the line for the next phase, very clearly
 * showing that the phase transiton nodes are the only way to get between phases"): its rows placed across by core
 * `gridColumns`, what happens in it under its start line. Between two blocks a band: the gate that closes the phase
 * above docked in it, centred and between both lines (design study 21, drawn B; transitions.ts), and the next phase's
 * title at its foot, just above that phase's start line. The start sits in the band above the first phase so, and the
 * end below the last, in the middle.
 *
 * The lines are laid out with the steps (#78, Jason, 2026-10-22: "i mean generalize this, there should be a clean
 * algorithm to untangle this stuff so that there are not crossings"): each line straight down past every row on its way
 * in a lane of its own (core places it with the steps), turning across only in the gap between two rows, each gap a
 * channel with a track for each line turning across it (channels.ts) and as tall as its tracks need. So two lines meet
 * only in a gap where their ends come in one order above and the other below: the crossings the rows were ordered to
 * have fewest of. A line turns across only inside a phase's white block, below its description and above its end line
 * (Jason, 2026-10-22: "keep all the tree lines below the greeen line so they don't over lap headings"): the lines out
 * of a gate fan out under the description of the phase it opens, those into a gate converge above the end line of the
 * phase it closes, and a line crosses a phase's line only straight up and down at its gate.
 */

export const TILE_W = 152;
export const TILE_H = 176;
/**
 * A task done early's small tile (design study 31, §7: `.tile.half`, its picture smaller): 156 against 176, room under its
 * name for the step it is done for ("For Engineering") and at its foot for where it stands, a line or two, as every
 * tile's (slice 4's final review: never its ring's colour alone). At the foot of its row, so its feet line up with the
 * tiles beside it and the lines out of it leave where theirs do; the lines into it meet its own top.
 */
export const SMALL_H = 156;

/** A step's box in a row whose top is `y`: a tile's, or a task done early's, at the row's foot (`SMALL_H`). */
export const tileBox = (s: Pick<TileStep, 'early'> | undefined, x: number, y: number, h = TILE_H): Box =>
  s?.early && h === TILE_H ? { x, y: y + TILE_H - SMALL_H, w: TILE_W, h: SMALL_H } : { x, y, w: TILE_W, h };
/** Between two tiles side by side: room for a line to run down between them. */
export const GAP_X = 32;
/** Between two rows: room for the lines to run across. */
export const GAP_Y = 72;
/** A row of steps left out alone: the height of its dots, the line running on through them. */
export const DOT_H = 32;
/** Above a row of dots: the line from the step before runs straight into them. */
export const DOT_GAP = 22;
/** A phase's title: its number and name and its facts, at the foot of the band above its start line (P-17). */
export const TITLE_H = 50;
/** What happens in a phase, under its start line. */
const WHAT_H = 30;
/** Above and below a step docked in a band, between it and the lines either side. */
export const BAND_PAD = 26;
const PAD_TOP = 24;
const PAD_BOTTOM = 28;
const PAD_X = 24;
/** Between the start (not docked) and the phases. */
const END_GAP = 60;
/** A long line's lane: this far from the side of a step it passes, and from the lane beside it. */
export const LANE_SIDE = 16;
export const LANE_GAP = 14;

/** How far apart the board's rows are placed across, in columns (core `gridColumns`). */
export const BOARD_SPACE: GridSpace = { steps: 1, side: (TILE_W / 2 + LANE_SIDE) / (TILE_W + GAP_X), lanes: LANE_GAP / (TILE_W + GAP_X) };

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface BoardLayout {
  width: number;
  height: number;
  cols: number;
  boxes: Map<string, Box>;
  /**
   * Each phase's block, from its start line (`y`) to its end line (`y + h`); its title's top in the band above it
   * (`head`); the step docked in that band (the gate that closes the phase before, or the start), if any; the foot of
   * what happens in it, under its start line (`what`), above which no line turns across.
   */
  blocks: { phase: string | null; y: number; h: number; head: number; docked: string | null; what: number }[];
  /** The transitions: where each step comes in the run of phases, and the gates docked between them. */
  transit: Transit;
  /** Each line the board draws down from row to row, by its id (`lineId`): its points, before its turns are cut. */
  routes: Map<string, Pt[]>;
}

/** How many tiles fit across a width: an odd number, so the board has a middle column for its ends and its gates (P-17). */
export const colsFor = (width: number): number => {
  const fit = Math.max(1, Math.floor((width - 2 * PAD_X + GAP_X) / (TILE_W + GAP_X)));
  return fit % 2 ? fit : fit - 1;
};

/** The transitions, given the gates docked on the phases' headings (`docksOf`). */
function transitOf(phases: readonly TilePhase[], steps: readonly TileStep[], docks: ReadonlyMap<string, string>): Transit {
  const place = placesOf(phases, steps);
  const late = new Set(steps.filter((s) => s.late && !s.ghost).map((s) => s.key));
  const transit: Transit = { place, rank: placesOf(phases, steps, true), gate: new Map(), late };
  for (const docked of docks.values()) if (place.has(docked)) transit.gate.set(place.get(docked)!, docked);
  return transit;
}

/** A drawn line's id: the step it comes into, the step it leaves, and the option it leaves by. */
export const lineId = (l: Pick<TileLine, 'src' | 'dst' | 'option'>): string => `${l.dst}<${l.src}${l.option ? `:${l.option}` : ''}`;

/**
 * The waits the board draws as lines: through the transitions (`transit`: a line from a step to a step in a later phase
 * drawn from the gate docked before it, transitions.ts), without what a longer way already draws (core `directNeeds`;
 * study 10, lines C, the tree keeping them, study 12, shortcuts B).
 */
export function drawnWaits(lines: readonly TileLine[], drawnSteps: ReadonlySet<string>, transit?: Transit): TileLine[] {
  const boxed = lines.filter((l) => drawnSteps.has(l.src) && drawnSteps.has(l.dst));
  const shown = transit ? linesThrough(boxed, transit) : boxed;
  const direct = directNeeds([...drawnSteps].map((k) => ({ key: k, needs: shown.filter((l) => l.dst === k).map((l) => l.src) })));
  return shown.filter((l) => l.option !== null || (direct.get(l.dst) ?? []).includes(l.src));
}

/**
 * The board's account of the lines it draws, for a step's card that says whether a line is drawn (design study 31,
 * slice 5: "Not drawn on the board."): the steps put in their rows as the board puts them (core `gridRows`), the gates
 * docked between the phases from those rows (`docksOf`), and then any set of lines given drawn as the board draws them
 * (`drawnWaits`), through those transitions. The rows are worked out once, for the steps given.
 */
export function boardWaits(phases: readonly TilePhase[], steps: readonly TileStep[], keepEmpty = false): (lines: readonly TileLine[]) => TileLine[] {
  const keys = new Set(steps.map((s) => s.key));
  const grid: GridStep[] = steps.map((s) => ({ key: s.key, phase: s.phase, needs: needKeys(s, keys), ...(s.end ? { end: s.end } : {}) }));
  // Where the steps sit in their rows is all the docks read; the order across, which the lines would settle, is not.
  const got: { ranks?: GridRanks } = {};
  gridRows(
    phases.map((p) => ({ key: p.key, closedBy: p.closedBy })),
    grid,
    {
      keepEmpty,
      lines: (ranks) => {
        got.ranks = ranks;
        return [];
      },
    },
  );
  const transit = transitOf(phases, steps, got.ranks ? docksOf(phases, got.ranks, steps) : new Map());
  return (lines) => drawnWaits(lines, keys, transit);
}

/** The last steps placed across, kept while the page only changes width. */
let placed: {
  phases: readonly TilePhase[];
  steps: readonly TileStep[];
  lines: readonly TileLine[] | undefined;
  keepEmpty: boolean;
  rows: GridRows;
  plan: GridPlan;
} | null = null;

/** Straight out of a step's foot before a line turns across a gap, and straight into a step's top after: the room it takes. */
const LEAVE = 4 + BOARD_ROOM.clear + BOARD_ROOM.bevel;
const ARRIVE = 9 + BOARD_ROOM.head + BOARD_ROOM.clear + BOARD_ROOM.bevel;
/** Between two lines turning across one gap on tracks of their own; and across, between two on one track. */
export const TRACK = 7;
const TRACK_ROOM = 2 * BOARD_ROOM.bevel + 8;
/** Under a phase's description, before the first line turns across; above its end line, after the last. */
const TURN_PAD = 10;
const END_ROOM = 18;
/** A phase's description: as wide as this at most, about this wide a letter, this tall a line. */
const WHAT_W = 960;
const WHAT_CHAR = 7.2;
const WHAT_LINE = 21;

export function boardLayout(
  phases: readonly TilePhase[],
  steps: readonly TileStep[],
  width: number,
  opts: { keepEmpty?: boolean; lines?: readonly TileLine[] } = {},
): BoardLayout {
  const keys = new Set(steps.map((s) => s.key));
  const byKey = new Map(steps.map((s) => [s.key, s]));
  const dots = new Set(steps.filter((s) => s.dot).map((s) => s.key));
  const grid: GridStep[] = steps.map((s) => ({ key: s.key, phase: s.phase, needs: needKeys(s, keys), ...(s.end ? { end: s.end } : {}) }));
  const needsOf = new Map(grid.map((g) => [g.key, g.needs]));
  const after = new Map<string, string[]>();
  for (const g of grid) for (const n of g.needs) after.set(n, [...(after.get(n) ?? []), g.key]);
  const tileLines = opts.lines ?? linesOf(steps);
  // The rows ordered, and placed across, by the lines the board draws (boardLines), as it draws them (#78): the same
  // whatever the page's width, so worked out once for the same steps and lines.
  const same = placed && placed.phases === phases && placed.steps === steps && placed.lines === opts.lines && placed.keepEmpty === Boolean(opts.keepEmpty);
  if (!same) {
    const rows = gridRows(
      phases.map((p) => ({ key: p.key, closedBy: p.closedBy })),
      grid,
      {
        keepEmpty: opts.keepEmpty,
        lines: (ranks) => drawnWaits(tileLines, keys, transitOf(phases, steps, docksOf(phases, ranks, steps))).map((l) => [l.src, l.dst] as const),
      },
    );
    placed = { phases, steps, lines: opts.lines, keepEmpty: Boolean(opts.keepEmpty), rows, plan: gridColumns(rows, 1, BOARD_SPACE) };
  }
  const { rows, plan } = placed!;
  const cols = plan.cols;
  // The gates docked between the phases: the start above the first, each phase's closing gate above the next.
  const docks = docksOf(phases, rows, steps);
  const gridW = cols * TILE_W + (cols - 1) * GAP_X;
  const wide = Math.max(width, Math.ceil(gridW) + 2 * PAD_X);
  const left0 = Math.max(PAD_X, (wide - gridW) / 2);
  const colX = (c: number) => Math.round(left0 + c * (TILE_W + GAP_X));
  // The middle, where the ends and the gates between the phases sit.
  const centre = Math.round(left0 + (gridW - TILE_W) / 2);
  const docked = new Set(docks.values());

  // Across: each step's left edge, each lane's middle.
  const layerOf = new Map<string, number>();
  rows.layers.forEach((l, i) => l.keys.forEach((k) => layerOf.set(k, i)));
  const leftOf = new Map<string, number>();
  if (plan.start) leftOf.set(plan.start.key, centre);
  if (plan.end) leftOf.set(plan.end.key, centre);
  for (const b of plan.blocks) for (const l of b.lines) l.keys.forEach((k, i) => leftOf.set(k, docked.has(k) ? centre : colX(l.cols[i]!)));
  const laneX = new Map<string, number>();
  const coreLine = new Map(rows.lines.map((l) => [`${l.from}>${l.to}`, l]));
  for (const lane of plan.lanes) coreLine.get(`${lane.from}>${lane.to}`)?.via.forEach((v, i) => laneX.set(v, colX(lane.via[i]!.col) + TILE_W / 2));

  // Each line the board draws, down from the row of the step it leaves to the row of the step it comes into: where it is
  // across in each row on the way (its lanes', core `gridColumns`), and the gap between each two it turns across in.
  const transit = transitOf(phases, steps, docks);
  const ways: { id: string; rows: number[]; xs: number[]; items: string[] }[] = [];
  for (const l of drawnWaits(tileLines, keys, transit)) {
    const [a, b] = [layerOf.get(l.src), layerOf.get(l.dst)];
    if (a === undefined || b === undefined || a >= b) continue;
    const via = b - a === 1 ? [] : coreLine.get(`${l.src}>${l.dst}`)?.via;
    if (!via || via.length !== b - a - 1 || via.some((v) => !laneX.has(v))) continue;
    const s = byKey.get(l.src);
    const options = s?.options ?? [];
    const i = l.option ? options.findIndex((o) => o.option === l.option) : -1;
    const out = leftOf.get(l.src)! + (i >= 0 ? handleSpot(i, options.length) : 0.5) * TILE_W;
    ways.push({
      id: lineId(l),
      rows: Array.from({ length: b - a + 1 }, (_, k) => a + k),
      xs: [out, ...via.map((v) => laneX.get(v)!), leftOf.get(l.dst)! + TILE_W / 2],
      items: [i >= 0 ? `${l.src}:${l.option}` : l.src, ...via, l.dst],
    });
  }
  // Each gap a channel: the lines turning across it each on a track (channels.ts), and the gap as tall as they need.
  const tracks = rows.layers.map(() => 0);
  const trackOf = new Map<string, number>();
  rows.layers.forEach((_, g) => {
    const runs: ChannelRun[] = ways.flatMap((w) => {
      const k = w.rows.indexOf(g);
      if (k < 0 || k + 1 >= w.rows.length || Math.abs(w.xs[k]! - w.xs[k + 1]!) < 0.5) return [];
      return [{ id: w.id, top: w.xs[k]!, from: w.items[k]!, bottom: w.xs[k + 1]!, to: w.items[k + 1]! }];
    });
    const { track, tracks: n } = channelTracks(runs, TRACK_ROOM);
    tracks[g] = n;
    for (const [id, t] of track) trackOf.set(`${id}@${g}`, t);
  });
  /** The height a gap's tracks take, from the first to the last. */
  const spread = (g: number) => Math.max(0, tracks[g]! - 1) * TRACK;
  const layerAt = (group: number, row: number) => rows.layers.findIndex((l) => typeof l.at !== 'string' && l.at.group === group && l.at.row === row);
  /** How far down a phase's block its description comes: lines turn across only below it (Jason, 2026-10-22). */
  const whatOf = new Map(phases.map((p) => [p.key, p.what]));
  const described = (phase: string | null) => {
    const what = phase !== null ? (whatOf.get(phase) ?? '') : '';
    if (!what) return WHAT_H;
    const perLine = Math.max(20, Math.floor((Math.min(WHAT_W, wide) - 48) / WHAT_CHAR));
    return Math.max(WHAT_H, 10 + Math.ceil(what.length / perLine) * WHAT_LINE);
  };

  const boxes = new Map<string, Box>();
  const blocks: BoardLayout['blocks'] = [];
  /** Each of core's rows, top and foot as drawn. */
  const span = new Map<number, { top: number; bottom: number }>();
  /** Where each gap's first track runs, under the row above it: every track below it, a track apart. */
  const floor = new Map<number, number>();
  /** The row last drawn, and its foot. */
  let prev: { layer: number; bottom: number } | null = null;
  const drawRow = (layer: number, top: number, bottom: number) => {
    span.set(layer, { top, bottom });
    prev = { layer, bottom };
  };
  /**
   * A row's top, at least `top`: low enough that the lines turning across the gap above it do so at `from` or lower
   * (under a phase's description, where the row is its phase's first) and come straight down into it after.
   */
  const rowTop = (layer: number, top: number, from = -Infinity) => {
    if (!prev || prev.layer !== layer - 1) return top;
    const g = prev.layer;
    const first = Math.max(prev.bottom + LEAVE, from);
    floor.set(g, first);
    return tracks[g] ? Math.max(top, first + spread(g) + ARRIVE) : top;
  };
  /**
   * A phase's foot, at least `foot`: low enough that the lines turning across under its last row towards the step
   * below its end line (the gate docked there, or the end) do so inside it, above the line.
   */
  const footFor = (layer: number, foot: number) => {
    if (!prev || prev.layer !== layer - 1) return foot;
    const g = prev.layer;
    floor.set(g, prev.bottom + LEAVE);
    return tracks[g] ? Math.max(foot, prev.bottom + LEAVE + spread(g) + END_ROOM) : foot;
  };
  let y = 8;
  const firstDock = plan.blocks[0]?.phase != null ? docks.get(plan.blocks[0].phase) : undefined;
  if (plan.start && firstDock !== plan.start.key) {
    // Not docked on the first phase: on its own above it all.
    boxes.set(plan.start.key, { x: centre, y: y + 10, w: TILE_W, h: TILE_H });
    drawRow(layerOf.get(plan.start.key)!, y + 10, y + 10 + TILE_H);
    y += 10 + TILE_H + END_GAP;
  }
  for (const [bi, b] of plan.blocks.entries()) {
    const dock = b.phase !== null ? (docks.get(b.phase) ?? null) : null;
    // The band above it: the step docked in it, centred between the lines; the phase's title at its foot.
    let band = TITLE_H + 10;
    if (dock) {
      boxes.set(dock, { x: centre, y: y + BAND_PAD, w: TILE_W, h: TILE_H });
      drawRow(layerOf.get(dock)!, y + BAND_PAD, y + BAND_PAD + TILE_H);
      band = Math.max(band, BAND_PAD * 2 + TILE_H);
    }
    const top = y + band;
    const what = described(b.phase);
    // Its closing gate docked in the next band is drawn there, not in it.
    const next = plan.blocks[bi + 1]?.phase;
    const closer = next != null ? docks.get(next) : undefined;
    const lines = closer ? b.lines.filter((l) => !(l.keys.length === 1 && l.keys[0] === closer)) : b.lines;
    let cy = top + what + PAD_TOP;
    // A dot in a plain chain sits tight between the step before it and the one after it, line, dot, line and arrow
    // (design study 18); wherever a line has to turn, or another runs past, the gap is a whole one, so the lines keep
    // their room.
    const tight = (above: (typeof lines)[number], below: (typeof lines)[number]): boolean => {
      if (above.keys.length !== 1 || below.keys.length !== 1 || Math.abs(above.cols[0]! - below.cols[0]!) > 1e-6) return false;
      if (rows.layers[layerAt(bi, above.row)]?.keys.length !== 1 || rows.layers[layerAt(bi, below.row)]?.keys.length !== 1) return false;
      const [a, z] = [above.keys[0]!, below.keys[0]!];
      if (!dots.has(a) && !dots.has(z)) return false;
      // The one waits on the other alone, and nothing else comes out of the one above.
      return (needsOf.get(z) ?? []).join() === a && (after.get(a) ?? []).join() === z;
    };
    lines.forEach((line, i) => {
      const dotRow = line.keys.every((k) => dots.has(k));
      if (i > 0) cy += !tight(lines[i - 1]!, line) ? GAP_Y : dotRow ? DOT_GAP : DOT_GAP + 24;
      const layer = layerAt(bi, line.row);
      // The first row's lines in from above turn under the phase's heading and description, never on its line.
      cy = i === 0 ? rowTop(layer, cy, top + what + TURN_PAD) : rowTop(layer, cy);
      const rowH = dotRow ? DOT_H : TILE_H;
      line.keys.forEach((k) => boxes.set(k, tileBox(byKey.get(k), leftOf.get(k)!, cy, rowH)));
      drawRow(layer, cy, cy + rowH);
      cy += rowH;
    });
    let h = lines.length ? cy - top + PAD_BOTTOM : what + PAD_TOP + 64;
    if (!lines.length) {
      // A phase with nothing in it: a line passing it runs through its block.
      const layer = layerAt(bi, 0);
      if (layer >= 0) drawRow(layer, top, top + h);
    }
    // The lines down to its closing gate, or to the end, turn inside it, above its end line.
    const below = closer ? layerOf.get(closer) : bi === plan.blocks.length - 1 && plan.end ? layerOf.get(plan.end.key) : undefined;
    if (lines.length && below !== undefined) h = footFor(below, top + h) - top;
    blocks.push({ phase: b.phase, y: top, h, head: top - TITLE_H, docked: dock, what: top + what });
    y = top + h;
  }
  if (plan.end) {
    // Below the last phase, in the middle.
    y += BAND_PAD;
    boxes.set(plan.end.key, { x: centre, y, w: TILE_W, h: TILE_H });
    drawRow(layerOf.get(plan.end.key)!, y, y + TILE_H);
    y += TILE_H;
  }
  // Each line as points: straight down out of its step, across its gap on its track where it turns, straight down past
  // each row in its lane, and into its step.
  const routes = new Map<string, Pt[]>();
  for (const w of ways) {
    const first = span.get(w.rows[0]!);
    const last = span.get(w.rows.at(-1)!);
    if (!first || !last || w.rows.some((r) => !span.has(r))) continue;
    const pts: Pt[] = [[w.xs[0]!, first.bottom + 4]];
    for (let k = 0; k + 1 < w.rows.length; k++) {
      const t = trackOf.get(`${w.id}@${w.rows[k]}`);
      if (t === undefined) continue;
      const ty = (floor.get(w.rows[k]!) ?? span.get(w.rows[k]!)!.bottom + LEAVE) + t * TRACK;
      pts.push([w.xs[k]!, ty], [w.xs[k + 1]!, ty]);
    }
    // Into the top of the step it comes into: a small tile's own, lower in its row (`tileBox`).
    pts.push([w.xs.at(-1)!, (boxes.get(w.items.at(-1)!)?.y ?? last.top) - 9]);
    routes.set(w.id, pts);
  }
  return { width: wide, height: y + 24, cols, boxes, blocks, transit, routes };
}

/** Where a line leaves a step: one of a decision's handles along its foot, else the middle of its foot. */
export function outPoint(b: Box, s: Pick<TileStep, 'options'>, option: string | null): Pt {
  const opts = s.options ?? [];
  const i = option ? opts.findIndex((o) => o.option === option) : -1;
  if (i >= 0) return [b.x + handleSpot(i, opts.length) * b.w, b.y + b.h + 4];
  return [b.x + b.w / 2, b.y + b.h + 4];
}

/** Where along a step's foot a decision's way on hangs: two at a quarter in, more spread evenly. */
export const handleSpot = (i: number, count: number): number => (count === 1 ? 0.5 : count === 2 ? [0.27, 0.73][i]! : (i + 1) / (count + 1));

/** The arrow's tip, above the top of the step that waits. */
export const inPoint = (b: Box): Pt => [b.x + b.w / 2, b.y - 9];

/** The arrow's head where a line comes into the top of a step. */
export const arrowHead = ([x, y]: Pt) => `M${x} ${y} L${x - 4} ${y - 8} L${x + 4} ${y - 8} Z`;

export interface DrawnLine extends TileLine {
  id: string;
  /**
   * A late step's line back to an earlier phase (transitions.ts `isLateLine`): drawn straight across the headings
   * between, in bengara, the one line that crosses a transition (design study 21). Laid down its lanes as any line is;
   * from a need come late below to the step above that needs it, up a corridor between the steps (`routeLines`).
   */
  late: boolean;
  d: string;
  head: Pt;
  mid: Pt;
}

/**
 * The lines to draw: the waits the board draws (`drawnWaits`), each as the layout laid it (`BoardLayout.routes`), its
 * turns cut at 45°. A line from a gate docked between two phases leaves its foot, as any step's does, down into the
 * phase it opens (P-17). A late step's line back to an earlier phase is the one drawn across the phases' lines, marked
 * `late` (design study 21); one back up the board, or any the layout did not lay, goes round the steps in its way
 * (`routeLines`).
 */
export function boardLines(steps: readonly TileStep[], lines: readonly TileLine[], board: Pick<BoardLayout, 'boxes'> & Partial<Pick<BoardLayout, 'transit' | 'width' | 'routes'>>): DrawnLine[] {
  const { boxes, transit, width, routes } = board;
  const byKey = new Map(steps.map((s) => [s.key, s]));
  const kept = drawnWaits(lines, new Set(boxes.keys()), transit);
  const laid = kept.map((l) => routes?.get(lineId(l)));
  const wants = kept.flatMap((l, i) => {
    if (laid[i]) return [];
    const to = boxes.get(l.dst)!;
    return [{ id: lineId(l), src: l.src, dst: l.dst, from: outPoint(boxes.get(l.src)!, byKey.get(l.src) ?? {}, l.option), to: inPoint(to) }];
  });
  const routeBoxes = new Map<string, RouteBox>([...boxes].map(([k, b]) => [k, { ...b, tabs: 0 }]));
  const routed = wants.length ? routeLines(wants, routeBoxes, BOARD_ROOM, width) : new Map();
  return kept.map((l, i) => {
    const id = lineId(l);
    const pts = laid[i];
    if (!pts) {
      const r = routed.get(id)!;
      return { ...l, id, d: r.d, head: inPoint(boxes.get(l.dst)!), mid: r.mid, late: Boolean(l.late) };
    }
    const drawn = bevelled(pts, BOARD_ROOM.bevel);
    // Its menu hangs from the middle of its longest run.
    let mid: Pt = drawn[0]!;
    let longest = -1;
    for (let k = 1; k < drawn.length; k++) {
      const [a, b] = [drawn[k - 1]!, drawn[k]!];
      const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
      if (len > longest) {
        longest = len;
        mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
      }
    }
    return { ...l, id, d: pathOf(drawn), head: pts.at(-1)!, mid, late: Boolean(l.late) };
  });
}

/** The card of a step opened: as wide as this, unless the page asks for wider (the master's, L-38), less a margin on a narrower page. */
const CARD_W = 720;
/** A row's label above it ("2 steps back"). */
const ROW_LABEL = 26;
const MARGIN = 28;

export interface ChainLine {
  id: string;
  src: string;
  dst: string;
  d: string;
  head: Pt;
  /** The wait it stands for, which can be clicked; none for a line to a step only offered after it. */
  line: TileLine | null;
  /** To a step only offered after it: dashed. */
  offered: boolean;
}

export interface ChainLayout {
  height: number;
  /** The steps of the chain, and those offered after it. */
  boxes: Map<string, Box>;
  /** The card standing for the step opened; its height is the card's own, measured. */
  card: Box;
  labels: { y: number; text: string }[];
  /** The steps offered after it (`after`): drawn dashed, with what they carry, and never opened from here. */
  offered: Set<string>;
  lines: ChainLine[];
}

/**
 * A step opened, in pixels (design study 10, open; study 12, open A): every row of what it waits on above it, all the
 * way back, and every row of what it opens below it, all the way on (Jason, 2026-10-07: "you should show the full
 * chain"; core `chainRows`), a row wider than the page carried on under itself; the card in the middle; under it, on a
 * job, what the tree offers after it. The chain's lines without what a longer way already draws, routed in the gaps.
 */
export function chainLayout(
  steps: readonly TileStep[],
  lines: readonly TileLine[],
  focus: string,
  width: number,
  cardH: number,
  after: { title: string; steps: readonly TileStep[] } | null = null,
  phases: readonly Pick<TilePhase, 'key' | 'closedBy'>[] = [],
  cardWidth: number = CARD_W,
): ChainLayout {
  const byKey = new Map([...steps, ...(after?.steps ?? [])].map((s) => [s.key, s]));
  // Its whole chain, through the gates between phases where a wait is on a step of an earlier phase (transitions.ts).
  const graph = chainThrough(steps, lines, phases, focus);
  const chain = chainRows(graph.nodes, focus);
  const cols = colsFor(width);
  const cut = (rows: readonly string[][]) => rows.flatMap((r) => (r.length <= cols ? [r] : Array.from({ length: Math.ceil(r.length / cols) }, (_, i) => r.slice(i * cols, (i + 1) * cols))));
  const above = cut(chain.needs).reverse();
  const below = cut(chain.unlocks);
  const extra = after?.steps.length ? cut([after.steps.map((s) => s.key)]) : [];
  const rowX = (n: number) => Math.round((width - (n * TILE_W + (n - 1) * GAP_X)) / 2);
  const boxes = new Map<string, Box>();
  const labels: ChainLayout['labels'] = [];
  const place = (r: readonly string[], y: number) => r.forEach((k, j) => boxes.set(k, tileBox(byKey.get(k), rowX(r.length) + j * (TILE_W + GAP_X), y)));
  let y = MARGIN;
  above.forEach((r, i) => {
    labels.push({ y, text: `${above.length - i} ${above.length - i === 1 ? 'step' : 'steps'} back` });
    place(r, y + ROW_LABEL);
    y += TILE_H + GAP_Y;
  });
  const cardW = Math.min(cardWidth, width - 32);
  const card: Box = { x: Math.round((width - cardW) / 2), y: y + 8, w: cardW, h: cardH };
  y = card.y + cardH + GAP_Y - 24;
  below.forEach((r, i) => {
    labels.push({ y, text: i === 0 ? 'What it opens' : `${i + 1} steps on` });
    place(r, y + ROW_LABEL);
    y += TILE_H + GAP_Y;
  });
  const offered = new Set<string>();
  extra.forEach((r, i) => {
    if (i === 0) labels.push({ y, text: after!.title });
    place(r, y + (i === 0 ? ROW_LABEL : 0));
    for (const k of r) offered.add(k);
    y += TILE_H + GAP_Y;
  });

  // The chain's lines, dark, among the steps drawn, the card standing for the step opened; without what a longer way
  // already draws (study 10, lines C), as on the whole tree. A leg through a gate carries no wait to take out.
  const drawn = new Set([focus, ...boxes.keys()].filter((k) => !offered.has(k)));
  const inChain = graph.edges.filter((e) => (e.line || e.through) && drawn.has(e.src) && drawn.has(e.dst));
  const direct = directNeeds([...drawn].map((k) => ({ key: k, needs: inChain.filter((e) => e.dst === k).map((e) => e.src) })));
  const kept = inChain.filter((e) => (e.line?.option ?? null) !== null || (direct.get(e.dst) ?? []).includes(e.src));
  const at = (k: string) => (k === focus ? card : boxes.get(k)!);
  const cardFoot: Pt = [card.x + card.w / 2, card.y + card.h + 4];
  const wants = [
    ...kept.map((e) => ({
      id: `${e.dst}<${e.src}${e.line?.option ? `:${e.line.option}` : ''}`,
      src: e.src,
      dst: e.dst,
      from: e.src === focus ? cardFoot : outPoint(at(e.src), byKey.get(e.src) ?? {}, e.line?.option ?? null),
      to: inPoint(at(e.dst)),
      line: e.line,
    })),
    ...[...offered].map((k) => ({ id: `${k}<${focus}`, src: focus, dst: k, from: cardFoot, to: inPoint(boxes.get(k)!), line: null })),
  ];
  const routeBoxes = new Map<string, RouteBox>([...boxes].map(([k, b]) => [k, { ...b, tabs: 0 }]));
  routeBoxes.set(focus, { ...card, tabs: 0 });
  const routed = routeLines(wants, routeBoxes, BOARD_ROOM, width);
  return {
    height: y,
    boxes,
    card,
    labels,
    offered,
    lines: wants.flatMap((w) => {
      const r = routed.get(w.id);
      return r ? [{ id: w.id, src: w.src, dst: w.dst, d: r.d, head: w.to, line: w.line, offered: offered.has(w.dst) }] : [];
    }),
  };
}

/** What a click lands on that is not the paper: the card, a step, a line, or anything that does something. */
const NOT_PAPER = '[data-card], [data-node], [data-line], button, a, input, textarea, select, label';

/** A press or a click on the paper round an opened chain: on none of those, and still on the page (not redrawn away under it). */
export const onPaper = (t: EventTarget | null): boolean => {
  const el = t as Partial<Element> | null;
  return typeof el?.closest === 'function' && el.isConnected !== false && !el.closest(NOT_PAPER);
};
